import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tray, FilePdf, Eye, CheckCircle } from "@phosphor-icons/react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }) : "");

const openFile = async (id) => {
  try {
    const { data } = await api.get(`/mailbox/messages/${id}/file`, { responseType: "blob" });
    window.open(URL.createObjectURL(data), "_blank");
  } catch { toast.error("Vorschau nicht möglich"); }
};

function ScanRow({ it, busy, onPick }) {
  return (
    <div className="flex items-center gap-3 border border-slate-200 rounded-md px-3 py-2 hover:bg-slate-50" data-testid={`mailbox-scan-${it.id}`}>
      <FilePdf size={20} className="text-rose-600 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{it.subject || it.file?.name || "Scan"}</div>
        <div className="text-[11px] text-slate-500 truncate">
          {fmt(it.received_at)} · {it.kind === "scan" || it.file ? it.file?.name : `${it.from_name || it.from_email || ""}${(it.mailbox || "").startsWith("shared:") ? ` · ${it.mailbox.slice(7)}` : ""}`}{it.ai?.doc_type_label ? ` · ${it.ai.doc_type_label}` : ""}
          {it.used_as_signed_scan && <span className="ml-1 text-emerald-700">· bereits verwendet</span>}
        </div>
      </div>
      {it.file && <button type="button" onClick={() => openFile(it.id)} className="text-slate-500 hover:text-slate-900" title="Ansehen" data-testid={`mailbox-scan-view-${it.id}`}><Eye size={16} /></button>}
      <Button size="sm" disabled={busy} onClick={() => onPick(it)} className="h-7 gap-1 bg-slate-900 hover:bg-slate-800" data-testid={`mailbox-scan-pick-${it.id}`}>
        <CheckCircle size={13} /> Übernehmen
      </Button>
    </div>
  );
}

// Unterschriebenes Original aus dem Postfach (Scan-Eingang) auswählen.
// endpoint(mid) liefert die POST-URL; onPicked(data) erhält die Server-Antwort.
export function MailboxScanPicker({ endpoint, onPicked, size = "sm", testId = "mailbox-scan-picker", allKinds = false,
  title = "Scan aus dem Postfach übernehmen", label = "Aus Postfach wählen" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => (allKinds
      ? api.get("/mailbox/messages", { params: { status: "new,suggested", q: q || undefined } })
      : api.get("/messages/signed-scans/mailbox", { params: { q: q || undefined } }))
      .then(({ data }) => setItems(data.items)).catch(() => setItems([])), 250);
    return () => clearTimeout(t);
  }, [open, q, allKinds]);

  const pick = async (it) => {
    setBusy(true);
    try {
      const { data } = await api.post(endpoint(it.id));
      toast.success(data.pages ? `Scan aus dem Postfach übernommen (${data.pages} Seite${data.pages === 1 ? "" : "n"})` : "Aus dem Postfach übernommen");
      onPicked?.(data);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan konnte nicht übernommen werden");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size={size} variant="outline" onClick={() => setOpen(true)} className="gap-1.5" data-testid={testId}>
        <Tray size={14} /> {label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{allKinds ? "Offene E-Mails und Scans aus allen Postfächern. Der Eintrag wird mit Anhängen übernommen und im Postfach als zugeordnet markiert." : "Wählen Sie das eingescannte, unterschriebene Original. Es wird im Postfach als zugeordnet markiert."}</DialogDescription>
          </DialogHeader>
          <Input placeholder="Suchen (Betreff, Dateiname, Inhalt)…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testId}-search`} />
          <div className="space-y-1.5 max-h-[50vh] overflow-y-auto pr-1">
            {items === null && <div className="text-sm text-slate-500 py-6 text-center">Lädt…</div>}
            {items?.length === 0 && <div className="text-sm text-slate-500 py-6 text-center" data-testid={`${testId}-empty`}>Keine Scans im Postfach gefunden.</div>}
            {items?.map((it) => <ScanRow key={it.id} it={it} busy={busy} onPick={pick} />)}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
